import { useCallback } from 'react';
import { toast } from 'sonner';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { classifyOrgArtifact } from '../models/orgArtifactKind';
import { useScenariosStore } from '../../../core/assistant/stores/scenariosStore';
import { useAtlasUnifiedChanges } from './useAtlasUnifiedChanges';
import { useOrgPlanningScenarioStore } from '../stores/orgPlanningScenarioStore';
import { atlasDraftRequiresProtectedAuthoring } from '../services/atlasDraftAuthoringMode';

/**
 * Editing the live org chart happens **in a scenario** — the same scenario
 * machinery the rest of Atlas uses, on the same diagram. The first edit
 * auto-creates a scenario and enters edit mode; from there every command is
 * recorded into the scenario's delta by `ScenarioAwareCommand` instead of
 * mutating the chart, the canvas renders the scenario projection, and the
 * scenario can be committed to the org model ("Commit to org").
 *
 * This mirrors the existing succession flow
 * (`NormalizeSuccessionTargetCommand`), which already auto-creates a scenario
 * on first edit. No fork, no second diagram, no new concept.
 */
export function useLiveChartScenarioGate(input: {
  diagramId: string | null | undefined;
  /** Planning charts are already an explicit draft surface. */
  forceScenario?: boolean;
}) {
  const diagramInfo = useDiagramInfo(input.diagramId || undefined);
  const isLiveChart = classifyOrgArtifact(diagramInfo.data?.viewDefinition) === 'live';
  const shouldUseScenario = isLiveChart || input.forceScenario === true;
  const unified = useAtlasUnifiedChanges();

  /**
   * Ensure edits land in a scenario before the caller executes its command.
   *
   * Returns whether it is safe to dispatch. On a non-live chart that is always
   * true (the command executes normally). On a live chart it is true only once
   * the scenario is actually in edit mode — otherwise `ScenarioAwareCommand`
   * would not divert the command and it would mutate a projection-locked chart.
   * Callers that only change a field can ignore the result; anything
   * structural must not dispatch on false.
   */
  const ensureScenarioForEdit = useCallback((): boolean => {
    if (!shouldUseScenario) return true;
    if (unified && input.diagramId) {
      useOrgPlanningScenarioStore.getState().beginEditing(input.diagramId, input.forceScenario ? 'scenario' : 'live');
      return true;
    }
    if (atlasDraftRequiresProtectedAuthoring(input.diagramId)) {
      toast.error('Finish or resolve the saved draft in Review changes before starting a legacy canvas edit.');
      return false;
    }
    let store = useScenariosStore.getState();

    if (!store.activeScenarioId) {
      const scenario = store.createScenario('Draft changes');
      useScenariosStore.getState().setActiveScenario(scenario.id);
      store = useScenariosStore.getState();
      toast.message('Editing in a scenario', {
        description: 'Your edits stay in “Draft changes” until you apply them to the org chart.',
      });
    }

    if (store.scenarioState !== 'EDITING_SCENARIO') {
      const result = store.enterEditMode();
      if (!result.success) {
        toast.error('Could not start editing', { description: result.error });
        return false;
      }
    }
    return useScenariosStore.getState().scenarioState === 'EDITING_SCENARIO';
  }, [input.diagramId, input.forceScenario, shouldUseScenario, unified]);

  return { isLiveChart: shouldUseScenario, ensureScenarioForEdit };
}
